import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Tabs, Tab } from '@mui/material';

// ─── Time Slot wise Order Summary ──────────────────────────────────────
const TimeSlotOrderSummary = ({ data = [], hubName }) => {
    const [activeTab, setActiveTab] = useState(0);

    const EMIRATES_TABS = [
        { label: 'ALL', key: 'ALL' },
        { label: 'SHJ', key: 'SHJ' },
        { label: 'DXB', key: 'DXB' },
        { label: 'AJM', key: 'AJM' },
        { label: 'UAQ', key: 'UAQ' },
        { label: 'AUH', key: 'AUH' },
        { label: 'RAK', key: 'RAK' },
        { label: 'FUJ', key: 'FUJ' },
    ];

    // Detect unique time slots from data, fallback to standard if none found
    const dynamicTimeSlots = Array.from(new Set(data.map(item => item.timeSlot))).filter(Boolean);
    const TIME_SLOTS_LABELS = dynamicTimeSlots.length > 0 ? dynamicTimeSlots : [
        '8am to 10am', '10am to 12pm', '12am to 2pm', '2pm to 4pm',
        '4pm to 6pm', '6pm to 8pm', '8pm to 10pm', '10pm to 12am'
    ];

    const METRIC_COLS = [
        { key: 'toBeAssigned', label: 'TB' },
        { key: 'onTheRoad', label: 'OR' },
        { key: 'delivered', label: 'DV' },
        { key: 'cancelled', label: 'CN' },
        { key: 'total', label: 'Total' },
        { key: 'otdPercentage', label: 'OD' },
    ];

    const currentEmirate = EMIRATES_TABS[activeTab].key;

    // Filter data based on active tab
    // If ALL, we want to show each emirate as a row
    const emirateList = currentEmirate === 'ALL'
        ? EMIRATES_TABS.filter(t => t.key !== 'ALL').map(t => t.key)
        : [currentEmirate];

    const normalizeEmirate = (name) => {
        if (!name) return '';
        const n = name.trim().toLowerCase();
        if (n === 'sharjah' || n === 'shj') return 'shj';
        if (n === 'dubai' || n === 'dxb') return 'dxb';
        if (n === 'ajman' || n === 'ajm') return 'ajm';
        if (n === 'umm al quwain' || n === 'uaq' || n === 'um al quwain') return 'uaq';
        if (n === 'abu dhabi' || n === 'auh' || n === 'abu dh.' || n === 'abu dh') return 'auh';
        if (n === 'ras al khaimah' || n === 'rak') return 'rak';
        if (n === 'fujairah' || n === 'fhj' || n === 'fujeirah' || n === 'fuj') return 'fuj';
        if (n === 'all') return 'all';
        return n;
    };

    // Build a lookup map for the table
    // map[emirate][timeSlot] = dataObject
    const lookup = {};
    (Array.isArray(data) ? data : []).forEach(item => {
        const em = normalizeEmirate(item.emirateName);
        const ts = item.timeSlot;
        if (!lookup[em]) lookup[em] = {};
        lookup[em][ts] = item;
    });

    const th = (bg = '#32CA73', extra = {}) => ({
        padding: '3px 5px', textAlign: 'center', fontWeight: 'bold', fontSize: 10,
        color: '#fff', backgroundColor: bg, border: '1px solid #28a05c', whiteSpace: 'nowrap', ...extra
    });
    const td = (shaded = false, isFirst = false) => ({
        padding: '3px 5px', textAlign: isFirst ? 'left' : 'center', fontSize: 10,
        fontWeight: isFirst ? 'bold' : 'normal', backgroundColor: shaded ? '#e2e6ea' : '#fff',
        border: '1px solid #dde1e6', color: '#333', whiteSpace: 'nowrap'
    });

    return (
        <Card variant="outlined" sx={{ width: '100%', mt: 2, borderRadius: 2, borderColor: '#d0d0d0' }}>
            <CardContent sx={{ padding: '12px !important' }}>
                <Typography variant="h6" style={{ fontSize: 14, fontWeight: 'bold', color: '#32CA73', textAlign: 'left', borderBottom: '2px solid #32CA73', paddingBottom: 6, marginBottom: 6 }}>
                    {hubName} - TIME SLOT WISE ORDER SUMMARY
                </Typography>

                {/* Tab strip - matching screenshot exactly */}
                <Box sx={{ borderBottom: '1px solid #cfd8e3', mb: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        TabIndicatorProps={{ style: { backgroundColor: '#32CA73', height: 3 } }}
                        sx={{
                            minHeight: 32,
                            '& .MuiTab-root': { minHeight: 32, fontSize: 11, fontWeight: 'bold', padding: '4px 12px', color: '#555', textTransform: 'none' },
                            '& .Mui-selected': { color: '#1a2b4a !important' },
                            '& .MuiTab-root:first-of-type': {
                                backgroundColor: activeTab === 0 ? '#1a2b4a' : 'transparent',
                                color: activeTab === 0 ? '#fff !important' : '#555',
                                borderRadius: '4px 4px 0 0'
                            },
                        }}
                    >
                        {EMIRATES_TABS.map(t => <Tab key={t.key} label={t.label} />)}
                    </Tabs>
                </Box>

                {/* Legend */}
                <Typography sx={{ fontSize: 10, color: '#666', mb: 0.5 }}>
                    TB = To be Assigned&nbsp;&nbsp;OR = On the Road&nbsp;&nbsp;DV = Delivered&nbsp;&nbsp;CA = Cancelled&nbsp;&nbsp;OD = On Time Delivery %
                </Typography>

                {/* The Complex Nested Header Table */}
                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', minWidth: '100%', tableLayout: 'auto' }}>
                        <thead>
                            <tr>
                                <th rowSpan={2} style={{ ...th(), textAlign: 'left', minWidth: 80 }}>Rider</th>
                                {TIME_SLOTS_LABELS.map(slot => (
                                    <th key={slot} colSpan={METRIC_COLS.length} style={th('#1a2b4a')}>{slot}</th>
                                ))}
                            </tr>
                            <tr>
                                {TIME_SLOTS_LABELS.map(slot =>
                                    METRIC_COLS.map((m) => (
                                        <th key={slot + m.key} style={th('#32CA73')}>{m.label}</th>
                                    ))
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {emirateList.map((emirate, rowIdx) => (
                                <tr key={emirate} style={{ backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#f7f9fc' }}>
                                    <td style={td(false, true)}>{emirate}</td>
                                    {TIME_SLOTS_LABELS.map(slot =>
                                        METRIC_COLS.map(m => {
                                            const val = lookup[normalizeEmirate(emirate)]?.[slot]?.[m.key] ?? 0;
                                            const isShaded = m.key === 'total';
                                            return (
                                                <td key={slot + m.key} style={td(isShaded)}>
                                                    {val}
                                                </td>
                                            );
                                        })
                                    )}
                                </tr>
                            ))}
                            {emirateList.length === 0 && (
                                <tr>
                                    <td colSpan={1 + TIME_SLOTS_LABELS.length * METRIC_COLS.length} style={{ ...td(), textAlign: 'center', padding: 12, color: '#999' }}>
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Box>
            </CardContent>
        </Card>
    );
};

export default TimeSlotOrderSummary;
